import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Chip,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { EditParams } from '../../pages/WorkDone/ManageWorkDone';
import { getFullUsersList } from '../../services/user.service';
import { workerDisplayName } from '../../utils/utilityFunctions';
import { fetchAllProjects, getProjectTypeAssociation } from '../../services/project.service';
import { fetchAllPrecincts } from '../../services/precinct.service';
import { fetchAllBlocks } from '../../services/block.service';
import { fetchAllTypes, fetchAllSubTypesByType, fetchAllSubValuesBySubType } from '../../services/type.service';
import { getWorkItemReviewIssues } from '../../utils/workDoneReview';

interface Props {
	show: boolean;
	params: EditParams;
	onSubmit: (payload: EditParams) => Promise<void>;
	onClose: () => void;
}
interface Option {
	id: number;
	name: string;
	location?: string | null;
}
interface ProjectOption extends Option {
	isHip: boolean;
}
interface Association {
	typeId: number;
	clientId: number;
	clientName: string;
}
interface BlockOption {
	id: number;
	number: string | number;
	levelFrom: number;
	levelTo: number;
	unitFrom: number;
	unitTo: number;
	unitNumberPattern: 'continuous' | 'odd' | 'even';
}
interface ValueOption {
	id: number;
	value: string;
}

const localDateTime = (value: string) => (dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DDTHH:mm') : '');

export const EditWorkDoneDialog = ({ show, params, onSubmit, onClose }: Props) => {
	const [form, setForm] = useState<EditParams>({ ...params, performedAt: localDateTime(params.performedAt) });
	const [workers, setWorkers] = useState<Option[]>([]);
	const [projects, setProjects] = useState<ProjectOption[]>([]);
	const [types, setTypes] = useState<Option[]>([]);
	const [associations, setAssociations] = useState<Association[]>([]);
	const [precincts, setPrecincts] = useState<Option[]>([]);
	const [blocks, setBlocks] = useState<BlockOption[]>([]);
	const [subTypes, setSubTypes] = useState<Option[]>([]);
	const [subValues, setSubValues] = useState<ValueOption[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showIssues, setShowIssues] = useState(false);
	const [showMoreOptions, setShowMoreOptions] = useState(false);
	const update = (changes: Partial<EditParams>) => setForm((current) => ({ ...current, ...changes }));

	const block = blocks.find((item) => item.id === form.blockId) ?? null;
	const isClientBillable = form.isHip && form.workCategory === 'normal';
	const hasExactUnit = form.blockId != null && form.level != null && form.unit != null;
	const showQuantity = !hasExactUnit || Number(form.quantity) !== 1;
	const selectedWorkerIds = [form.userId, ...form.helperUserIds].filter(
		(userId, index, values) => userId > 0 && values.indexOf(userId) === index
	);
	const updateWorkers = (workerIds: number[]) => {
		const uniqueWorkerIds = workerIds.filter((userId, index, values) => values.indexOf(userId) === index);
		const anchorUserId = uniqueWorkerIds.includes(form.userId) ? form.userId : uniqueWorkerIds[0] ?? 0;
		update({
			userId: anchorUserId,
			helperUserIds: uniqueWorkerIds.filter((userId) => userId !== anchorUserId),
		});
	};
	const clientOptions = useMemo(() => {
		const unique = new Map<number, Association>();
		associations.filter((item) => item.typeId === form.typeId).forEach((item) => unique.set(item.clientId, item));
		return Array.from(unique.values());
	}, [associations, form.typeId]);
	const typeOptions = useMemo(
		() => types.filter((type) => associations.some((item) => item.typeId === type.id)),
		[types, associations]
	);
	const selectedClientName =
		clientOptions.find((client) => client.clientId === form.clientId)?.clientName ??
		associations.find((client) => client.clientId === form.clientId)?.clientName;
	const issues = getWorkItemReviewIssues(
		{
			userId: form.userId,
			helperUserIds: form.helperUserIds,
			projectId: form.projectId,
			clientId: form.clientId,
			precinctId: form.precinctId,
			blockId: form.blockId,
			blockLevelFrom: block?.levelFrom ?? null,
			blockLevelTo: block?.levelTo ?? null,
			blockUnitFrom: block?.unitFrom ?? null,
			blockUnitTo: block?.unitTo ?? null,
			blockUnitNumberPattern: block?.unitNumberPattern ?? null,
			typeId: form.typeId,
			subTypeId: form.subTypeId,
			subTypeValueId: form.subTypeValueId,
			level: form.level,
			unit: form.unit,
			quantity: form.quantity,
			quantityUnit: form.quantityUnit,
			performedAt: form.performedAt,
			workCategory: form.workCategory,
			isClientBillable,
			isWorkerCreditable: true,
			reportedLocationText: form.reportedLocationText,
		},
		{ requireStructuredLocation: form.isHip }
	);

	const loadProject = async (projectId: number) => {
		const [precinctResponse, associationResponse] = await Promise.all([
			fetchAllPrecincts(`precinct/getAll?projectId=${projectId}`),
			getProjectTypeAssociation('project/typeAssociation/getAll', projectId),
		]);
		setPrecincts(
			(precinctResponse?.data?.data ?? []).map((item: any) => ({
				id: item.id,
				name: item.name,
				location: item.location ?? null,
			}))
		);
		setAssociations(
			(associationResponse?.data?.data ?? []).map((item: any) => ({
				typeId: item.typeId,
				clientId: item.clientId,
				clientName: item.clientName ?? `Client ${item.clientId}`,
			}))
		);
	};
	const loadBlocks = async (precinctId: number) => {
		const response = await fetchAllBlocks(`block/getAll?precinctId=${precinctId}`);
		setBlocks(
			(response?.data?.data ?? []).map((item: any) => ({
				id: item.id,
				number: item.number,
				levelFrom: Number(item.levelFrom ?? 1),
				levelTo: Number(item.levelTo),
				unitFrom: Number(item.unitFrom),
				unitTo: Number(item.unitTo),
				unitNumberPattern: item.unitNumberPattern ?? 'continuous',
			}))
		);
	};
	const loadSubTypes = async (typeId: number) => {
		const response = await fetchAllSubTypesByType(`type/subtype/name/getall?typeId=${typeId}`);
		setSubTypes((response?.data?.data ?? []).map((item: any) => ({ id: item.id, name: item.name })));
	};
	const loadSubValues = async (subTypeId: number) => {
		const response = await fetchAllSubValuesBySubType(`type/subtype/value/getall?subtypeId=${subTypeId}`);
		setSubValues((response?.data?.data ?? []).map((item: any) => ({ id: item.id, value: item.value })));
	};

	useEffect(() => {
		if (!show) return;
		let active = true;
		setForm({ ...params, performedAt: localDateTime(params.performedAt) });
		setShowIssues(false);
		setShowMoreOptions(false);
		setLoading(true);
		(async () => {
			try {
				const [workerResponse, projectResponse, typeResponse] = await Promise.all([
					getFullUsersList('worker'),
					fetchAllProjects('project/all'),
					fetchAllTypes('type/all'),
				]);
				if (!active) return;
				setWorkers(
					(workerResponse?.data?.rows ?? []).map((item: any) => ({ id: item.id, name: workerDisplayName(item) }))
				);
				setProjects(
					(projectResponse?.data?.data ?? []).map((item: any) => ({
						id: item.id,
						name: item.name,
						isHip: !!item.isHip,
					}))
				);
				setTypes((typeResponse?.data?.data ?? []).map((item: any) => ({ id: item.id, name: item.name })));
				await Promise.all([
					loadProject(params.projectId),
					params.precinctId ? loadBlocks(params.precinctId) : Promise.resolve(),
					loadSubTypes(params.typeId),
					loadSubValues(params.subTypeId),
				]);
			} catch (error: any) {
				if (active) toast.error(error?.response?.data?.message || 'Could not load the WorkDone form.');
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, params.id]);

	const submit = async () => {
		setShowIssues(true);
		if (issues.length) return;
		setSaving(true);
		try {
			await onSubmit({ ...form, performedAt: new Date(form.performedAt).toISOString() });
		} finally {
			setSaving(false);
		}
	};

	const select = (
		label: string,
		value: any,
		options: React.ReactNode,
		onChange: (value: any) => void,
		disabled = false
	) => (
		<FormControl size='small' disabled={disabled} fullWidth>
			<InputLabel>{label}</InputLabel>
			<Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
				{options}
			</Select>
		</FormControl>
	);

	return (
		<Dialog open={show} onClose={saving ? undefined : onClose} fullWidth maxWidth='md'>
			<DialogTitle sx={{ pb: 1 }}>Edit work done</DialogTitle>
			<DialogContent dividers sx={{ py: 2 }}>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Stack direction='row' spacing={0.75} alignItems='center' sx={{ mb: 1.5 }}>
							<Chip label={form.isHip ? 'HIP' : 'Non-HIP'} size='small' variant='outlined' />
							<Chip
								label={isClientBillable ? 'Client billable' : 'Non-billable'}
								size='small'
								color={isClientBillable ? 'success' : 'default'}
								variant='outlined'
							/>
						</Stack>
						{showIssues && issues.length > 0 && (
							<Alert severity='error' sx={{ mb: 2 }}>
								{issues.join(' • ')}
							</Alert>
						)}
						<Typography variant='subtitle2'>Work details</Typography>
						<Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
							Project and type determine the available client, subtype and size.
						</Typography>
						<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
							{select(
								'Project',
								form.projectId,
								projects.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.name}
									</MenuItem>
								)),
								async (value) => {
									const projectId = Number(value);
									const project = projects.find((item) => item.id === projectId);
									update({
										projectId,
										isHip: !!project?.isHip,
										clientId: 0,
										precinctId: null,
										blockId: null,
										typeId: 0,
										subTypeId: 0,
										subTypeValueId: 0,
										level: null,
										unit: null,
									});
									setBlocks([]);
									setSubTypes([]);
									setSubValues([]);
									await loadProject(projectId);
								}
							)}
							{select(
								'Type of work',
								form.typeId || '',
								typeOptions.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.name}
									</MenuItem>
								)),
								async (value) => {
									const typeId = Number(value);
									const clients = associations.filter((item) => item.typeId === typeId);
									update({
										typeId,
										clientId: clients.length === 1 ? clients[0].clientId : 0,
										subTypeId: 0,
										subTypeValueId: 0,
									});
									setSubValues([]);
									await loadSubTypes(typeId);
								}
							)}
							{clientOptions.length > 1 ? (
								select(
									'Client',
									form.clientId || '',
									clientOptions.map((item) => (
										<MenuItem key={item.clientId} value={item.clientId}>
											{item.clientName}
										</MenuItem>
									)),
									(value) => update({ clientId: Number(value) }),
									!form.typeId
								)
							) : (
								<TextField
									size='small'
									label='Client'
									value={selectedClientName ?? ''}
									InputProps={{ readOnly: true }}
									disabled={!form.typeId}
								/>
							)}
							{select(
								'Subtype',
								form.subTypeId || '',
								subTypes.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.name}
									</MenuItem>
								)),
								async (value) => {
									const subTypeId = Number(value);
									update({ subTypeId, subTypeValueId: 0 });
									await loadSubValues(subTypeId);
								},
								!form.typeId
							)}
							{select(
								'Size / option',
								form.subTypeValueId || '',
								subValues.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.value}
									</MenuItem>
								)),
								(value) => update({ subTypeValueId: Number(value) }),
								!form.subTypeId
							)}
							<FormControl size='small' fullWidth>
								<InputLabel>Workers</InputLabel>
								<Select
									multiple
									label='Workers'
									value={selectedWorkerIds}
									onChange={(event) => updateWorkers((event.target.value as number[]).map(Number))}
									renderValue={(selected) =>
										(selected as number[])
											.map((id) => workers.find((worker) => worker.id === id)?.name ?? id)
											.join(', ')
									}>
									{workers.map((worker) => (
										<MenuItem key={worker.id} value={worker.id}>
											<Checkbox checked={selectedWorkerIds.includes(worker.id)} />
											{worker.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<Divider sx={{ my: 2 }} />
						<Typography variant='subtitle2' sx={{ mb: 1 }}>
							Where was it done?
						</Typography>
						<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
							{select(
								'Area',
								form.precinctId ?? '',
								[
									<MenuItem key='none' value=''>
										<em>None</em>
									</MenuItem>,
									...precincts.map((item) => (
										<MenuItem key={item.id} value={item.id}>
											{item.location || item.name}
										</MenuItem>
									)),
								],
								async (value) => {
									const precinctId = value === '' ? null : Number(value);
									update({ precinctId, blockId: null, level: null, unit: null });
									setBlocks([]);
									if (precinctId) await loadBlocks(precinctId);
								}
							)}
							{select(
								'Block',
								form.blockId ?? '',
								blocks.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.number}
									</MenuItem>
								)),
								(value) => update({ blockId: Number(value), level: null, unit: null }),
								!form.precinctId
							)}
							<TextField
								size='small'
								label='Level'
								type='number'
								value={form.level ?? ''}
								helperText={block ? `${block.levelFrom}-${block.levelTo}` : ''}
								onChange={(event) => update({ level: event.target.value === '' ? null : Number(event.target.value) })}
							/>
							<TextField
								size='small'
								label='Unit'
								type='number'
								value={form.unit ?? ''}
								helperText={
									block
										? `${block.unitFrom}-${block.unitTo}${
												block.unitNumberPattern !== 'continuous' ? `, ${block.unitNumberPattern}` : ''
										  }`
										: ''
								}
								onChange={(event) => update({ unit: event.target.value === '' ? null : Number(event.target.value) })}
							/>
							{showQuantity && (
								<>
									<TextField
										size='small'
										label='Quantity'
										type='number'
										inputProps={{ min: 0.01, step: 'any' }}
										value={form.quantity}
										onChange={(event) => update({ quantity: Number(event.target.value) })}
									/>
									<TextField
										size='small'
										label='Quantity unit'
										value={form.quantityUnit}
										onChange={(event) => update({ quantityUnit: event.target.value })}
									/>
								</>
							)}
						</Box>
						{!hasExactUnit && (
							<TextField
								size='small'
								fullWidth
								label='Area description (when there is no exact unit)'
								value={form.reportedLocationText}
								onChange={(event) => update({ reportedLocationText: event.target.value })}
								sx={{ mt: 1.5 }}
							/>
						)}

						<Button size='small' onClick={() => setShowMoreOptions((current) => !current)} sx={{ mt: 1.5, px: 0 }}>
							{showMoreOptions ? 'Hide more options' : 'More options'}
						</Button>
						{showMoreOptions && (
							<Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
									{select(
										'Work category',
										form.workCategory,
										[
											<MenuItem key='normal' value='normal'>
												Standard work
											</MenuItem>,
											<MenuItem key='free_redo' value='free_redo'>
												Free redo
											</MenuItem>,
											<MenuItem key='rectify' value='rectify'>
												Rectification
											</MenuItem>,
											<MenuItem key='fabrication' value='fabrication'>
												Fabrication
											</MenuItem>,
										],
										(value) => update({ workCategory: value })
									)}
									<TextField
										size='small'
										label='Work date and time'
										type='datetime-local'
										InputLabelProps={{ shrink: true }}
										value={form.performedAt}
										onChange={(event) => update({ performedAt: event.target.value })}
									/>
								</Box>
							</Box>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button variant='contained' onClick={submit} disabled={loading || saving}>
					{saving ? <CircularProgress size={20} color='inherit' /> : 'Save changes'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
